import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from '../core/AddButton';
import CancelButton from '../core/CancelButton';
import Deprecated from '../core/Deprecated';
import MenuSelector from '../core/MenuSelector';
import PrimaryButton from '../core/PrimaryButton';
import ProjectLanguageSelector from '../core/ProjectLanguageSelector';
import SearchSelect from '../core/SearchSelect';
import SecondaryButton from '../core/SecondaryButton';
import { globalStyles as styles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import { DATATYPES } from '../core/utils/constants';
import { getDatatypeMenuOptionsByIRI, getResourceLabel, isSupportedDatatype } from '../core/utils/utils';
import { ALIAS_LABELS } from './constants';
import ComponentLinkList from './ontologyproject/ComponentLinkList';
import TabbedSubmenu from '../core/TabbedSubmenu';

class DatatypeDialog extends React.Component {

    constructor(props) {
        super(props);
        const isDatatypeSupported = isSupportedDatatype(props.datatype);
        const datatype = props.datatype && isDatatypeSupported ?
            props.datatype
            :
            undefined;
        this.state = {
            open: props.open,
            lang: props.viewLang,
            selected: datatype,
            datatype
        };
    }

    computeSearch = (value, lang) => {
        let graphList = [this.props.graph];
        let search = [
            {
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: ["http://www.w3.org/2000/01/rdf-schema#Datatype"]
            },
            {
                predicate: "http://www.w3.org/2000/01/rdf-schema#label",
                ...(value !== "") && {
                    value: value,
                    isExactMatch: false
                }
            }
        ];
        let fullSearch =
        {
            search,
            graph: graphList,
            excludeSystemNamespaces: true,
            language: lang,
            exclude: this.props.existingValues,
            ignoreNoLanguage: lang !== "und",
            fast: value === "",
            basicSort: value === "",
            pageSize: 20,
            predicates: [
                "http://www.w3.org/2000/01/rdf-schema#label",
                "http://www.w3.org/2002/07/owl#deprecated",
                "http://www.w3.org/2000/01/rdf-schema#isDefinedBy",
                "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                "https://grafsync.graphifi.com/schema/gs/visibility"
            ]
        };
        return fullSearch;
    };

    changeLanguage = (lang) => this.setState({ lang });

    addDatatype = () => {
        this.setState({ open: false });
        this.props.onAddDatatype(this.state.datatype);
        this.setState({ datatype: DATATYPES.STRING });
    };

    addFoundDatatype = (id) => {
        this.setState({ open: false });
        this.props.onAddDatatype(id);
        this.setState({ datatype: DATATYPES.STRING });
    };

    exitAddDatatype = () => {
        this.setState({
            open: false,
            datatype: this.props.datatype,
            error: false
        });
        if (this.props.onExitDatatype) this.props.onExitDatatype();
    };

    handleClickOpen = () => this.setState({ open: true });

    handleDatatypeChange = (datatype) => this.setState({ datatype });

    render() {
        const { title, disabled, useButton, buttonTitle, viewLang, sourceComponent, allowCustom } = this.props;
        const { datatype, open, selected, lang } = this.state;

        if (!open) {
            return useButton ?
                <SecondaryButton dataTest="button-choose" onClick={() => this.handleClickOpen()}>{buttonTitle || title}</SecondaryButton>
                :
                <AddButton dataTest="add-datatype" title="Add a datatype" onClick={this.handleClickOpen} />;
        }

        const { classes, existingValues } = this.props;
        const menuOptions = getDatatypeMenuOptionsByIRI([existingValues]);

        let tabs = ["Standard Datatypes"];
        if (allowCustom) tabs.push("Custom Datatypes");

        return (
            <React.Fragment>
                {!this.props.open && useButton &&
                    <SecondaryButton dataTest="button-choose">{buttonTitle || title}</SecondaryButton>
                }
                {!this.props.open && !useButton &&
                    <AddButton dataTest="add-datatype" title="Add a datatype" />
                }
                <Dialog
                    data-test="datatype-dialog"
                    maxWidth="md"
                    fullWidth
                    classes={{
                        paper: classes.paper,
                        paperWidthMd: classes.mediumDialog
                    }}
                    className={classes.dialog}
                    open={this.state.open}
                    onClose={this.exitAddDatatype}
                    aria-labelledby="datatype-dialog-title">
                    <DialogTitle disableTypography className={classes.mainHeading} id="datatype-dialog-title">{title}</DialogTitle>
                    <DialogContent style={{ height: '480px' }}>
                        <form autoComplete="off" noValidate="novalidate">
                            <TabbedSubmenu tabs={tabs}>
                                <TitledPanel dataTest="datatype-selection" height="full" margin title="Datatype">
                                    <MenuSelector
                                        dataTest="datatype-selector"
                                        autoFocus={false}
                                        disabled={disabled}
                                        selected={selected}
                                        handleChange={this.handleDatatypeChange}
                                        hintWord="datatype"
                                        options={menuOptions} />
                                </TitledPanel>
                                <div style={{ height: 320 }}>
                                    <TitledPanel title="Language">
                                        <ProjectLanguageSelector selectedLang={viewLang} includeLabel={false} onSelect={this.changeLanguage} />
                                    </TitledPanel>
                                    <SearchSelect
                                        key={lang}
                                        dataTest="resource-label"
                                        focus={true}
                                        maxLength={250}
                                        sortByLength={true}
                                        wrapped={true}
                                        search={(value, lang) => this.computeSearch(value, lang)}
                                        listSecondIcon={(result) => {
                                            if (result.deprecated) return <Deprecated />;
                                            return null;
                                        }}
                                        listSelectionDisplay={(result, lang) => {
                                            const label = getResourceLabel(result, "label", lang);
                                            return label;
                                        }}
                                        listDisplay={(result, lang) => {
                                            const label = getResourceLabel(result, "label", lang);
                                            const resultTypes = Array.isArray(result.type) ? result.type : [result.type];
                                            const aliasLabel = resultTypes.filter(resultType => resultType in ALIAS_LABELS)
                                                .map(rt => ALIAS_LABELS[rt])
                                                .sort()
                                                .join(" AND ");
                                            return <div>
                                                <Typography variant="body2">
                                                    <Tooltip title={result.id}>
                                                        <span data-test="result-text-words">{label}</span>
                                                    </Tooltip>
                                                </Typography>
                                                <Typography data-test="alias-list" variant="caption">
                                                    {aliasLabel}
                                                </Typography>
                                                <ComponentLinkList
                                                    data={result}
                                                    includeIsDefinedBy={true}
                                                    graph={result.inNamedGraph}
                                                    context={sourceComponent}
                                                    defaultLang={lang}
                                                />
                                            </div>;
                                        }}
                                        sortProp="label"
                                        isClickableResult={(result) => !result.invalid && !result.deprecated}
                                        title="Enter text to search"
                                        lang={lang}
                                        chosen={this.addFoundDatatype} />
                                </div>
                            </TabbedSubmenu>
                        </form>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton onClick={this.exitAddDatatype} />
                        <PrimaryButton disabled={datatype === undefined} onClick={this.addDatatype}>Add</PrimaryButton>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

DatatypeDialog.defaultProps = {
    excludeDatatypes: [],
    open: false,
    disabled: false,
    useButon: false,
    allowCustom: true,
    title: "Add a datatype"
};

DatatypeDialog.propTypes = {
    dataTest: PropTypes.string,
    name: PropTypes.string.isRequired,
    allowCustom: PropTypes.bool,
    existingValues: PropTypes.array,
    open: PropTypes.bool,
    useButton: PropTypes.bool,
    datatype: PropTypes.string,
    title: PropTypes.string,
    graph: PropTypes.string.isRequired,
    buttonTitle: PropTypes.string,
    viewLang: PropTypes.string,
    disabled: PropTypes.bool,
    excludeDatatypes: PropTypes.array,
    onAddDatatype: PropTypes.func.isRequired,
    onExitDatatype: PropTypes.func,
    classes: PropTypes.object
};

export default withStyles(styles)(DatatypeDialog);
